import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, filter, map, type Observable } from 'rxjs';

import {
  type UserProfile,
  type AuthState,
  type LoginOptions,
  type LogoutOptions,
  OAuthEventType,
  AuthStorageKey,
} from './auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oauthService: OAuthService = inject(OAuthService);
  private readonly router: Router = inject(Router);

  private readonly authState: ReturnType<typeof signal<AuthState>> = signal<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  private readonly events$: BehaviorSubject<OAuthEvent | null> = new BehaviorSubject<OAuthEvent | null>(null);

  readonly isAuthenticated: Signal<boolean> = computed(() => this.authState().isAuthenticated);
  readonly isLoading: Signal<boolean> = computed(() => this.authState().isLoading);
  readonly user: Signal<UserProfile | null> = computed(() => this.authState().user);
  readonly error: Signal<string | null> = computed(() => this.authState().error);

  readonly tokenExpired$: Observable<boolean> = this.events$.pipe(
    filter((event: OAuthEvent | null) => event?.type === OAuthEventType.TOKEN_EXPIRES),
    map(() => true)
  );

  readonly tokenReceived$: Observable<boolean> = this.events$.pipe(
    filter((event: OAuthEvent | null) => event?.type === OAuthEventType.TOKEN_RECEIVED),
    map(() => true)
  );

  constructor() {
    this.setupOAuthEventHandlers();
    this.setupStorageListener();
    this.checkAuthState();
  }

  login(optionsOrReturnUrl?: LoginOptions | string): void {
    const options: LoginOptions =
      typeof optionsOrReturnUrl === 'string' ? { returnUrl: optionsOrReturnUrl } : optionsOrReturnUrl || {};

    if (options.returnUrl) {
      this.setReturnUrl(options.returnUrl);
    }
    this.oauthService.initLoginFlow(undefined, options.additionalParams);
  }

  logout(optionsOrReturnUrl?: LogoutOptions | string): void {
    const options: LogoutOptions =
      typeof optionsOrReturnUrl === 'string' ? { returnUrl: optionsOrReturnUrl } : optionsOrReturnUrl || {};

    this.clearStoredState();
    this.oauthService.logOut();

    if (options.returnUrl) {
      this.router.navigate([options.returnUrl]);
    }
  }

  async refreshToken(): Promise<boolean> {
    try {
      await this.oauthService.refreshToken();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
      return false;
    }
  }

  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  getIdToken(): string | null {
    return this.oauthService.getIdToken();
  }

  getReturnUrl(): string | null {
    try {
      const url: string | null = sessionStorage.getItem(AuthStorageKey.RETURN_URL);
      if (url) {
        sessionStorage.removeItem(AuthStorageKey.RETURN_URL);
      }
      return url;
    } catch (error) {
      console.error('Failed to retrieve return URL:', error);
      return null;
    }
  }

  private setReturnUrl(url: string): void {
    try {
      sessionStorage.setItem(AuthStorageKey.RETURN_URL, url);
    } catch (error) {
      console.error('Failed to store return URL:', error);
    }
  }

  private setupOAuthEventHandlers(): void {
    this.oauthService.events.subscribe((event: OAuthEvent) => {
      this.events$.next(event);

      switch (event.type) {
        case OAuthEventType.TOKEN_RECEIVED:
          this.updateAuthState();
          break;

        case OAuthEventType.TOKEN_EXPIRES:
          this.handleTokenExpiration();
          break;

        case OAuthEventType.TOKEN_ERROR:
        case OAuthEventType.TOKEN_REVOKE_ERROR:
          this.updateAuthState('Authentication error occurred');
          break;

        case OAuthEventType.LOGOUT:
          this.updateAuthState();
          break;
      }
    });
  }

  private setupStorageListener(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === AuthStorageKey.ACCESS_TOKEN || event.key === AuthStorageKey.ID_TOKEN) {
        this.checkAuthState();
      }
    });
  }

  private updateAuthState(error: string | null = null): void {
    const isAuthenticated: boolean = this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();
    const user: UserProfile | null = isAuthenticated ? this.getUserProfile() : null;

    this.authState.set({
      isAuthenticated,
      isLoading: false,
      user,
      error,
    });
  }

  private getUserProfile(): UserProfile | null {
    const claims: Record<string, unknown> = this.oauthService.getIdentityClaims();
    if (!claims) return null;

    return {
      sub: claims['sub'] as string,
      name: claims['name'] as string,
      email: claims['email'] as string,
      given_name: claims['given_name'] as string,
      family_name: claims['family_name'] as string,
      preferred_username: claims['preferred_username'] as string,
    };
  }

  private checkAuthState(): void {
    const isAuthenticated: boolean = this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();

    this.authState.update((state: AuthState) => ({
      ...state,
      isAuthenticated,
      isLoading: false,
      user: isAuthenticated ? this.getUserProfile() : null,
    }));
  }

  private handleTokenExpiration(): void {
    if (this.oauthService.getRefreshToken()) {
      this.refreshToken();
    } else {
      this.logout();
    }
  }

  private clearStoredState(): void {
    try {
      sessionStorage.removeItem(AuthStorageKey.RETURN_URL);
    } catch (error) {
      console.error('Failed to clear stored auth state:', error);
    }
  }
}
